import { useState } from 'react';
import ChartDisplay from './ChartDisplay';

interface ResultCardProps {
  result: {
    data: any[];
    sql: string;
    chartType: 'bar' | 'line' | 'pie';
  };
}

export default function ResultCard({ result }: ResultCardProps) {
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>(result.chartType);
  const [showSql, setShowSql] = useState(false);
  const [sqlContent, setSqlContent] = useState(result.sql);
  const [isEditing, setIsEditing] = useState(false);
  const [feedback, setFeedback] = useState<'like' | 'dislike' | null>(null);
  const [showDislikeModal, setShowDislikeModal] = useState(false);
  const [dislikeReason, setDislikeReason] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  const handleFeedback = (type: 'like' | 'dislike') => {
    if (type === 'like') {
      setFeedback(feedback === type ? null : type);
    } else {
      // 点击拉踩时打开弹窗
      setShowDislikeModal(true);
    }
  };

  const handleDislikeSubmit = () => {
    if (!dislikeReason.trim()) {
      return;
    }
    setFeedback('dislike');
    console.log('拉踩原因:', dislikeReason);
    setShowDislikeModal(false);
    setDislikeReason('');
  };

  const handleDislikeCancel = () => {
    setShowDislikeModal(false);
    setDislikeReason('');
  };

  const handleSqlSave = () => {
    setIsEditing(false);
    // 这里可以触发重新查询
    console.log('保存并重新查询:', sqlContent);
  };

  const handleReExecute = () => {
    console.log('重新执行查询:', sqlContent);
    // 这里可以触发重新查询逻辑
  };

  const handleCopySQL = async () => {
    try {
      await navigator.clipboard.writeText(sqlContent);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 获取表格列名
  const columns = result.data.length > 0 ? Object.keys(result.data[0]) : [];

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-bar-chart-box-line text-blue-600 text-sm"></i>
            </div>
            <span className="text-xs font-medium text-gray-900">查询结果</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleReExecute}
              className="px-2 py-1 flex items-center gap-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line text-blue-600 text-xs"></i>
              <span className="text-xs font-medium text-gray-700">重新执行</span>
            </button>
            <button
              onClick={() => setShowSql(!showSql)}
              className="px-2 py-1 flex items-center gap-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-code-s-slash-line text-blue-600 text-xs"></i>
              <span className="text-xs font-medium text-gray-700">查看 SQL</span>
            </button>
          </div>
        </div>

        {/* SQL Editor */}
        {showSql && (
          <div className="px-3 py-2 bg-gray-900 border-b border-gray-700">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-400">SQL 查询语句</span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleCopySQL}
                  className="px-2 py-1 bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
                >
                  <i className={`${isCopied ? 'ri-check-line' : 'ri-file-copy-line'} mr-0.5 text-xs`}></i>
                  {isCopied ? '已复制' : '复制'}
                </button>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-2 py-1 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
                  >
                    <i className="ri-edit-line mr-0.5 text-xs"></i>
                    编辑
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setSqlContent(result.sql);
                      }}
                      className="px-2 py-1 bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSqlSave}
                      className="px-2 py-1 bg-blue-500 text-white hover:bg-blue-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
                    >
                      <i className="ri-save-line mr-0.5 text-xs"></i>
                      保存并执行
                    </button>
                  </>
                )}
              </div>
            </div>
            <textarea
              value={sqlContent}
              onChange={(e) => setSqlContent(e.target.value)}
              disabled={!isEditing}
              className="w-full bg-gray-800 text-gray-100 text-xs font-mono p-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none resize-none"
              rows={3}
            />
          </div>
        )}

        {/* View Mode Selector */}
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-600 mr-1">查看方式:</span>
            <button
              onClick={() => setViewMode('table')}
              className={`px-2 py-1 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                viewMode === 'table'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-table-line mr-0.5 text-xs"></i>
              表格
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`px-2 py-1 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                viewMode === 'chart'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-bar-chart-line mr-0.5 text-xs"></i>
              图表
            </button>
          </div>
        </div>

        {/* Chart Type Selector - Only show when in chart mode */}
        {viewMode === 'chart' && (
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-600 mr-1">图表类型:</span>
              <button
                onClick={() => setChartType('bar')}
                className={`px-2 py-1 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  chartType === 'bar'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <i className="ri-bar-chart-line mr-0.5 text-xs"></i>
                柱状图
              </button>
              <button
                onClick={() => setChartType('line')}
                className={`px-2 py-1 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  chartType === 'line'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <i className="ri-line-chart-line mr-0.5 text-xs"></i>
                折线图
              </button>
              <button
                onClick={() => setChartType('pie')}
                className={`px-2 py-1 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  chartType === 'pie'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <i className="ri-pie-chart-line mr-0.5 text-xs"></i>
                饼图
              </button>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="p-4">
          {viewMode === 'table' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.data.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      {columns.map((column, colIndex) => (
                        <td
                          key={colIndex}
                          className="px-3 py-2 text-xs text-gray-900"
                        >
                          {row[column]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <ChartDisplay data={result.data} type={chartType} />
          )}
        </div>

        {/* Ambiguity Warning Tip */}
        <div className="px-4 pb-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-information-line text-amber-600 text-sm"></i>
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-amber-900 mb-1">提示：查询结果可能存在理解偏差</p>
                <p className="text-xs text-amber-800 leading-relaxed">
                  如果查询结果与您的预期不符，可能是因为问题描述存在歧义。建议您明确时间范围、具体指标、分类维度等信息后重新提问，以获得更准确的分析结果。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Summary & Actions */}
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <span className="text-xs text-gray-600">
            共查询到 <strong className="text-gray-900">{result.data.length}</strong> 条记录
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => handleFeedback('like')}
              className={`px-2 py-1 flex items-center gap-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                feedback === 'like' 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600'
              }`}
            >
              <i className="ri-thumb-up-line text-xs"></i>
              <span>点赞</span>
            </button>
            <button
              onClick={() => handleFeedback('dislike')}
              className={`px-2 py-1 flex items-center gap-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                feedback === 'dislike' 
                  ? 'bg-red-100 text-red-600' 
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600'
              }`}
            >
              <i className="ri-thumb-down-line text-xs"></i>
              <span>拉踩</span>
            </button>
            <div className="w-px h-4 bg-gray-300 mx-0.5"></div>
            <button className="px-2 py-1 bg-blue-500 text-white hover:bg-blue-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium">
              <i className="ri-download-line mr-0.5 text-xs"></i>
              导出数据
            </button>
          </div>
        </div>
      </div>

      {/* Dislike Reason Modal */}
      {showDislikeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">请告诉我们原因</h3>
                <button
                  onClick={handleDislikeCancel}
                  className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-gray-500 text-base"></i>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-4 py-4">
              <p className="text-xs text-gray-600 mb-2">您的反馈将帮助我们改进查询结果的质量</p>
              <textarea
                value={dislikeReason}
                onChange={(e) => setDislikeReason(e.target.value)}
                placeholder="请输入您不满意的原因..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none resize-none"
                rows={4}
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-xs text-gray-400">{dislikeReason.length}/500</span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button
                onClick={handleDislikeCancel}
                className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
              >
                取消
              </button>
              <button
                onClick={handleDislikeSubmit}
                disabled={!dislikeReason.trim()}
                className="px-3 py-1.5 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
              >
                提交反馈
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
